/**
 * Which way a popup opens, measured inside whatever would clip it.
 *
 * The rule was paid for by the `Select` on 2026-07-31: an absolutely
 * positioned popup is cut off at the edge of the nearest scrolling ancestor
 * long before the viewport has anything to say about it, and three of the
 * product's dropdowns live inside a modal whose body scrolls. Measuring the
 * window alone opens a list downward into a container with no room, which is
 * the one failure that makes a field at the foot of a form unusable.
 *
 * It lives here rather than inside either component because the scripted date
 * and time pickers (2026-08-04, ruling R5) need exactly the same measurement,
 * and two copies of a rule this fiddly have already been shown to drift.
 */

/** The boxes that would cut a popup off: every scrolling or hidden ancestor. */
function clippersOf(anchor: HTMLElement, axis: "overflowY" | "overflowX"): DOMRect[] {
  const boxes: DOMRect[] = []
  let node = anchor.parentElement
  while (node) {
    if (/(auto|hidden|scroll)/.test(getComputedStyle(node)[axis])) {
      boxes.push(node.getBoundingClientRect())
    }
    node = node.parentElement
  }
  return boxes
}

/**
 * True when the popup should hang above the anchor instead of below it.
 *
 * Downward is the default and stays the default when neither side has room:
 * a popup that flips up into a space just as short is only a different kind of
 * clipped, and down is where a reader's eye already is.
 */
export function shouldDropUp(anchor: HTMLElement | null, height: number): boolean {
  if (!anchor) return false
  const rect = anchor.getBoundingClientRect()
  let ceiling = 0
  let floor = window.innerHeight
  for (const box of clippersOf(anchor, "overflowY")) {
    ceiling = Math.max(ceiling, box.top)
    floor = Math.min(floor, box.bottom)
  }
  const below = floor - rect.bottom
  return below < height && rect.top - ceiling > below
}

/**
 * True when the popup should hang from the anchor's right edge instead of its
 * left, because a left-hung popup wider than its field would run off the side.
 *
 * The calendar is wider than the fields that carry it, and the date fields sit
 * in the right-hand column of two- and three-column grids, so this is not a
 * theoretical case. It only flips when the flip actually fits: running off the
 * left is no better than running off the right.
 */
export function shouldAlignEnd(anchor: HTMLElement | null, width: number): boolean {
  if (!anchor) return false
  const rect = anchor.getBoundingClientRect()
  let wall = window.innerWidth
  for (const box of clippersOf(anchor, "overflowX")) wall = Math.min(wall, box.right)
  return rect.left + width > wall && rect.right - width >= 0
}
